import camelcaseKeys from 'camelcase-keys'
import { format, setYear } from 'date-fns'
import throttle from 'lodash.throttle'

import { FAVORITE_TAG } from '@/modules/projects'
import type {
  Transaction,
  TransactionDTO,
} from '@/modules/projects/stores/types'
import {
  executeDelete,
  executeGet,
  executePatch,
  executePost,
} from '@/services/lukita'
import {
  addOrUpdateItems,
  byFavorite,
  parseCalendarDate,
  removeItems,
  updateItem,
  updateItems,
} from '@/utils'
import { type LoadMode, loadPages, type PageStart } from './paging'
import type {
  ProjectStoreCreator,
  TransactionsActions,
  TransactionsLoaders,
  TransactionsMutations,
} from './types'

const isCCPayment = (transaction: Omit<Transaction, 'isNotCCPayment'>) =>
  transaction.accountType === 'Credit card' &&
  (transaction.categoryDetail === 'CREDIT_CARD_PAYMENT' ||
    transaction.categoryDetail === 'Credit card payment')

const withCCPaymentFlag = (
  transaction: Omit<Transaction, 'isNotCCPayment'>,
): Transaction => ({
  ...transaction,
  isNotCCPayment: !isCCPayment(transaction),
})

export const transactionsActions: ProjectStoreCreator<TransactionsActions> = (
  set,
  get,
) => ({
  startLoadingTransactions: () => set({ loadingTransactions: true }),
  stopLoadingTransactions: () => set({ loadingTransactions: false }),

  setFilterByFavorites: (filterByFavorites) => {
    const slug = get().project?.slug
    if (!slug) return

    localStorage.setItem(`favorites-${slug}`, String(filterByFavorites))
    set({ filterByFavorites })
  },

  setTransactions: (transactions) =>
    set({ transactions: camelcaseKeys(transactions).map(withCCPaymentFlag) }),

  mergeTransactions: (transactions) =>
    set((state) => ({
      transactions: addOrUpdateItems(
        state.transactions,
        camelcaseKeys(transactions).map(withCCPaymentFlag),
      ),
    })),

  updateTransactions: (transactions) =>
    set((state) => ({
      transactions: updateItems(
        state.transactions,
        camelcaseKeys(transactions).map(withCCPaymentFlag),
      ),
    })),

  addTransactions: (transactions) =>
    set((state) => ({
      transactions: [
        ...state.transactions,
        ...camelcaseKeys(transactions).map(withCCPaymentFlag),
      ],
    })),

  unfavoriteTransaction: (transaction) =>
    get().actions.setTransaction({ ...transaction, tags: [] }),

  favoriteTransaction: (transaction) =>
    get().actions.setTransaction({ ...transaction, tags: [FAVORITE_TAG] }),

  setTransaction: (transaction) =>
    set((state) => ({
      transactions: updateItem(
        state.transactions,
        withCCPaymentFlag(camelcaseKeys(transaction)),
      ),
    })),

  deleteTransactions: (ids) =>
    set((state) => ({ transactions: removeItems(state.transactions, ids) })),
})

export const transactionsMutations: ProjectStoreCreator<
  TransactionsMutations
> = (set, get) => ({
  updateTransaction: async (id, body) => {
    // Don't gate the PATCH on the row being in the grid store: the review workspace edits rows it
    // loaded from the segment endpoint, which the grid may not hold. setTransaction is a no-op when
    // the row isn't loaded, but the edit still reaches the server.
    const project = get().project
    if (!project) return

    set({ mutating: true })
    const path = `/projects/${project.slug}/transactions/${id}`
    const response = await executePatch(path, body).finally(() =>
      set({ mutating: false }),
    )

    const updatedTransaction = await response.json()
    get().actions.setTransaction(updatedTransaction)
    return updatedTransaction
  },

  updateTransactions: async (transactions, body) => {
    set({ mutating: true })
    const project = get().project
    const ids = transactions.map(({ id }) => id)
    if (!ids.length || !project) return

    const path = `/projects/${project.slug}/transactions/`
    await executePatch(path, {
      ids,
      data: body,
    }).finally(() => set({ mutating: false }))

    const idSet = new Set(ids)
    const parsedBody = camelcaseKeys(body)

    const updateTransaction = (transaction: Transaction): Transaction => {
      const { year, ...updates } = parsedBody
      const updatedDate =
        year &&
        format(
          setYear(parseCalendarDate(transaction.date), Number(year)),
          'yyyy-MM-dd',
        )

      return {
        ...transaction,
        ...updates,
        ...(year && { date: updatedDate as string }),
      }
    }

    const updatedTransactions = get()
      .transactions.filter(({ id }) => idSet.has(id))
      .map(updateTransaction)

    get().actions.updateTransactions(updatedTransactions)
  },

  updateFavoriteTransaction: async (transaction) => {
    const { favoriteTransaction, unfavoriteTransaction } = get().actions
    const isFavorited = byFavorite({ tags: transaction.tags })
    isFavorited
      ? unfavoriteTransaction(transaction)
      : favoriteTransaction(transaction)

    try {
      await get().mutations.updateTransaction(transaction.id, {
        tag: FAVORITE_TAG,
      })
    } catch (error) {
      isFavorited
        ? favoriteTransaction(transaction)
        : unfavoriteTransaction(transaction)
      throw error
    }
  },

  deleteTransactions: async (transactions) => {
    const project = get().project
    const ids = transactions.map(({ id }) => id)
    get().actions.deleteTransactions(ids)

    try {
      const path = `/projects/${project?.slug}/transactions`
      await executeDelete(path, { ids })
    } catch (error) {
      get().actions.addTransactions(transactions)
      throw error
    }
  },

  // Soft-delete: drop the rows from the grid right away, then confirm with the server. The cycle's
  // reconcile gap refreshes when lukita re-broadcasts the statement. Roll the rows back on failure.
  removeTransactions: async (transactions) => {
    const project = get().project
    const ids = transactions.map(({ id }) => id)
    get().actions.deleteTransactions(ids)

    try {
      await executePost(`/projects/${project?.slug}/transactions/remove`, {
        ids,
      })
    } catch (error) {
      get().actions.addTransactions(transactions)
      throw error
    }
  },

  // Reverse a removal: put the rows back, then confirm. Roll them out again on failure.
  restoreTransactions: async (transactions) => {
    const project = get().project
    const ids = transactions.map(({ id }) => id)
    get().actions.addTransactions(transactions)

    try {
      await executePost(`/projects/${project?.slug}/transactions/restore`, {
        ids,
      })
    } catch (error) {
      get().actions.deleteTransactions(ids)
      throw error
    }
  },

  createTransaction: async (body) => {
    set({ mutating: true })
    const project = get().project

    const response = await executePost(
      `/projects/${project?.slug}/transactions`,
      body,
    ).finally(() => set({ mutating: false }))

    const created = await response.json()
    get().actions.addTransactions([created])
    return withCCPaymentFlag(camelcaseKeys(created))
  },
})

interface LedgerLoad {
  slug: string
  mode: LoadMode
  start?: PageStart
}

// One page walk of the ledger. A failed page is recorded before the error goes on, so a screen
// that sums the ledger can say it is short instead of quietly adding up part of it.
const ledgerLoader: ProjectStoreCreator<(load: LedgerLoad) => Promise<void>> =
  (set, get) =>
  async ({ slug, mode, start }) => {
    const loadId = get().transactionsLoadId + 1
    const isStale = () => get().transactionsLoadId !== loadId
    set({ transactionsLoadId: loadId })

    const { setTransactions, mergeTransactions } = get().actions
    try {
      await loadPages<TransactionDTO>({
        path: `/projects/${slug}/transactions`,
        root: 'transactions',
        mode,
        start,
        isStale,
        store: (rows, { isFirst }) =>
          isFirst ? setTransactions(rows) : mergeTransactions(rows),
        onProgress: ({ hasMore }) =>
          set({ loadingTransactions: false, loadingMoreTransactions: hasMore }),
        onPageFailed: (failure) =>
          set({ ledgerIncomplete: failure, loadingTransactions: false }),
      })
      if (!isStale()) set({ ledgerIncomplete: null })
    } finally {
      if (!isStale()) set({ loadingMoreTransactions: false })
    }
  }

export const transactionsLoaders: ProjectStoreCreator<TransactionsLoaders> = (
  set,
  get,
  store,
) => {
  const loadLedger = ledgerLoader(set, get, store)

  return {
    fetchTransactions: throttle(async (slug, options) => {
      if (options?.isFile) {
        return executeGet(
          `/projects/${slug}/transactions.${options.format}`,
          options,
        )
      }

      const currentProject = get().project
      if (!currentProject || currentProject.slug !== slug) {
        get().actions.startLoadingTransactions()
      }

      const hasLoadedList =
        !get().loadingTransactions && !get().loadingMoreTransactions

      get().actions.setFilterByFavorites(
        localStorage.getItem(`favorites-${slug}`) === 'true',
      )

      await loadLedger({ slug, mode: hasLoadedList ? 'whole' : 'progressive' })
    }, 500),

    retryFailedLedgerPage: async () => {
      const slug = get().project?.slug
      const failure = get().ledgerIncomplete
      if (!slug || !failure) return

      set({ loadingMoreTransactions: true })
      await loadLedger({
        slug,
        mode: 'progressive',
        start: failure.resumeFrom,
      })
    },
  }
}
